import { parseEmbedOptions, parseEmbedWidgetId } from "@givehere/contracts";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { CheckInPage } from "./CheckInPage.js";
import { Checkout } from "./Checkout.js";
import { embedRouteFor } from "./embed/embed-frame.js";
import { EmbedGive } from "./embed/EmbedGive.js";
import { EmbedSignIn } from "./embed/EmbedSignIn.js";
import { EmbedWallet } from "./embed/EmbedWallet.js";
import { takeEventAccessToken } from "./event-access.js";
import { EventPage } from "./EventPage.js";
import { takeEventJoinToken } from "./EventJoin.js";
import { rememberArrival, rememberReferral } from "./gift-attribution.js";
import { InvitePage } from "./InvitePage.js";
import { WaitlistPage } from "./WaitlistPage.js";
import { relayGoogleCallback } from "./google-sign-in.js";
import { Home } from "./Home.js";
import { LegalPageView } from "./legal/LegalPageView.js";
import { legalPageFor } from "./legal/paths.js";
import { SiteFooter } from "./legal/SiteFooter.js";
import { OrgLanding } from "./OrgLanding.js";
import { PaymentRequestPage } from "./PaymentRequestPage.js";
import { TicketOrderView } from "./TicketOrderPage.js";
import { TicketsPage } from "./TicketsPage.js";
import "./styles.css";

/**
 * Routes, resolved from the path with no router library.
 *
 *   /g/<signed-context-token>  the checkout. The token selects organization, event, campaign and
 *                              suggested amount before anything renders, so the donor never picks
 *                              from a list and the page never asks for location (SPEC.md §5.1).
 *   /o/<organizationId>        the organization's own page — who they are, what they are raising
 *                              for, how far along. A plain id rather than a signed token,
 *                              because nothing here is a checkout: the page mints no context and
 *                              shows only what the public directory already shows.
 *   /e/<eventId>               the donor's event page. Shares its address with the hall's
 *                              projection, which is the same path plus `?projector=1` and is
 *                              served out of the live-display bundle instead — see `bundleFor`
 *                              in `infra/static-server.mjs`. One address, two audiences: a link
 *                              an operator forwards to a donor still opens something useful.
 *   /c/<eventId>               checking in, from the code on the wall. Its own first-segment
 *                              route rather than a query parameter on `/e/`, because a printed
 *                              code that loses its query string must not land silently on a
 *                              different page.
 *   /tickets/<eventId>         the public pre-event page: tiers, buy, share (DEC-016).
 *   /tix/<orderId>?t=<token>   a buyer's tickets, from the confirmation email.
 *   /r/<signed-request-token>  a payment request's "Give now" link (GH-845): the organization's
 *                              ask, then the give box priced from the request by the server.
 *   /terms, /privacy, /disclosures, /support, /account-deletion
 *                              the legal pages (GH-812), static and the same for every visitor.
 *   /                          the directory. For a donor who did not scan anything.
 *
 * A dependency-free switch because the routes are all decided by the first path segment. That
 * rule is the reason `/c/<eventId>` exists at all: `/e/<eventId>/check-in` would have required a
 * two-segment parse under a prefix this bundle does not even own the assets for.
 */
const path = window.location.pathname;

/*
 *   /embed/give/<organizationId>         the give box framed on another site (GH-847): no token
 *                                        in the address, no site footer, its own compact layout.
 *   /embed/give/<organizationId>/wallet  the first-party window it opens for Apple Pay.
 *   /embed/give/<organizationId>/sign-in the first-party window it opens for sign-in (GH-877).
 */
const embedRoute = embedRouteFor(path);

/*
 * Google's answer to "Continue with Google" (GH-859) lands on a registered return address of this
 * origin with the ID token in the fragment. That load is not a visit: hand the answer to the tab
 * that asked, close, and render nothing of the app — no directory fetch, no arrival recorded.
 */
const googleAnswer = relayGoogleCallback();

// Before any page renders: how this visit arrived (a table's code, the big screen's, the email
// button), kept for the checkout link this tab may mint later (GH-841). The give box sets its own
// tag on the server and takes none from its address.
if (!embedRoute && !googleAnswer) {
  rememberArrival();
  // A member's share code (`?ref=`, GH-864e), kept seven days for the checkout link a later visit
  // may mint. Attribution only; the server re-validates it.
  rememberReferral();
}
/*
 * GH-864e — membership pages, and the big screen's auto-invite:
 *
 *   /i/<code>    an invite a member shared: who sent it, the ways in, and the code.
 *   /waitlist    joining the line, and your place in it.
 *   ?join=<tok>  on /c/ or /e/: the display's QR auto-invite. Taken out of the address at once.
 */
const inviteCode = path.startsWith("/i/") ? decodeURIComponent(path.slice("/i/".length)) : null;
const waitlist = path === "/waitlist" || path === "/waitlist/";
/*
 * GH-871b — `?access=<token>` on /c/ (where the big screen's QR lands) or /e/: the event access
 * token that opens the event while membership is on. Kept in this tab's session storage for the
 * token's life and taken out of the address at once, before `?join=` is read.
 */
const eventPath = path.startsWith("/c/") || path.startsWith("/e/");
if (eventPath) takeEventAccessToken(decodeURIComponent(path.slice(3)));
const joinToken = eventPath ? takeEventJoinToken() : null;
const contextToken = path.startsWith("/g/") ? decodeURIComponent(path.slice(3)) : null;
const ticketsEventId = path.startsWith("/tickets/")
  ? decodeURIComponent(path.slice("/tickets/".length))
  : null;
const organizationId = path.startsWith("/o/") ? decodeURIComponent(path.slice("/o/".length)) : null;
const eventId = path.startsWith("/e/") ? decodeURIComponent(path.slice("/e/".length)) : null;
const checkInEventId = path.startsWith("/c/") ? decodeURIComponent(path.slice("/c/".length)) : null;
const orderMatch = path.startsWith("/tix/") ? decodeURIComponent(path.slice("/tix/".length)) : null;
const orderToken = new URLSearchParams(window.location.search).get("t");
const paymentRequestToken = path.startsWith("/r/")
  ? decodeURIComponent(path.slice("/r/".length))
  : null;
const legalPage = legalPageFor(path);

const page = legalPage ? (
  <LegalPageView page={legalPage} />
) : contextToken ? (
  <Checkout contextToken={contextToken} />
) : organizationId ? (
  <OrgLanding organizationId={organizationId} />
) : checkInEventId ? (
  <CheckInPage eventId={checkInEventId} joinToken={joinToken} />
) : eventId ? (
  <EventPage eventId={eventId} joinToken={joinToken} />
) : inviteCode !== null ? (
  <InvitePage code={inviteCode} />
) : waitlist ? (
  <WaitlistPage />
) : ticketsEventId ? (
  <TicketsPage eventId={ticketsEventId} />
) : orderMatch && orderToken ? (
  <TicketOrderView orderId={orderMatch} accessToken={orderToken} />
) : paymentRequestToken ? (
  <PaymentRequestPage token={paymentRequestToken} />
) : (
  <Home />
);

const root = document.getElementById("root");
if (root && googleAnswer) {
  // Shown only where the browser refused to let the window close itself.
  root.textContent = "You can close this window.";
} else if (root && embedRoute) {
  createRoot(root).render(
    <StrictMode>
      {embedRoute.wallet ? (
        <EmbedWallet organizationId={embedRoute.organizationId} />
      ) : embedRoute.signIn ? (
        <EmbedSignIn />
      ) : (
        <EmbedGive
          organizationId={embedRoute.organizationId}
          options={parseEmbedOptions(window.location.search)}
          widgetId={parseEmbedWidgetId(window.location.search)}
        />
      )}
    </StrictMode>,
  );
} else if (root && path.startsWith("/embed/")) {
  // An /embed/ address that is not a give box renders nothing — never the ordinary donor app,
  // which must not appear inside a frame (only exact /embed/give/<uuid> may be framed).
  root.textContent = "";
} else if (root) {
  // The site footer (GH-812) under every page, so the legal links are one tap from anywhere.
  createRoot(root).render(
    <StrictMode>
      {page}
      <SiteFooter />
    </StrictMode>,
  );
}
